import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';
import AdminEmailVerification from '@/components/admin/AdminEmailVerification';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [showVerify, setShowVerify] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    base44.auth.isAuthenticated().then(async (authed) => {
      if (authed) {
        try {
          const user = await base44.auth.me();
          if (user?.role === 'admin') {
            navigate('/admin/dashboard', { replace: true });
            return;
          }
        } catch {}
      }
      setChecking(false);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      await base44.auth.loginViaEmailPassword(email, password);
      const user = await base44.auth.me();
      if (user?.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        setError('This account does not have admin access.');
        try { await base44.auth.logout(); } catch {}
      }
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    }
    setLoading(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (showVerify) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="w-full max-w-md px-6 py-16">
          <AdminEmailVerification
            defaultEmail={email}
            onCancel={() => setShowVerify(false)}
            onDone={() => { setShowVerify(false); setVerified(true); setError(''); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
      <div className="w-full max-w-md px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="https://media.base44.com/images/public/69e7659143303e8153678f84/d1f090528_Asset1.png"
              alt="Motion Reality"
              className="h-10 w-auto"
            />
          </div>

          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 border border-primary/20 bg-primary/5 text-primary text-[10px] font-bold tracking-[0.2em] px-3 py-1.5">
              <ShieldCheck className="w-3 h-3" /> ADMIN AREA
            </span>
          </div>

          <h1 className="text-white font-black text-3xl md:text-4xl uppercase text-center mb-2">Admin Sign In</h1>
          <p className="text-white/30 text-sm text-center mb-8">Authorised personnel only.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">EMAIL ADDRESS</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60"
                placeholder="admin@email.com"
              />
            </div>

            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">PASSWORD</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60 pr-10"
                  placeholder="Your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {verified && !error && (
              <p className="text-green-400 text-sm bg-green-400/10 border border-green-400/20 px-4 py-3">
                Email verified — sign in with your password below.
              </p>
            )}

            {error && (
              <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-3">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-sm py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="text-white/30 text-xs text-center mb-3">Got a verification code by email?</p>
            <button
              type="button"
              onClick={() => { setError(''); setShowVerify(true); }}
              className="w-full border border-white/15 hover:border-primary/60 hover:bg-primary/10 text-white/70 hover:text-white font-bold text-xs tracking-widest py-3.5 transition-colors flex items-center justify-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" /> ENTER MY VERIFICATION CODE
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}